import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Truck, Package, CheckCircle, ArrowRight, FilePdf, Eye, Clock } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

/**
 * DeelLeveringModal — UI voor gedeeltelijke leveringen vanuit een bestelbon.
 *
 * 3-secties layout:
 *   1. BOVEN: Bestelling met voortgang per artikel (besteld/geleverd/te leveren)
 *   2. MIDDEN: Al gegenereerde leverbonnen (historiek)
 *   3. ONDER: Nieuwe levering samenstellen (artikel kiezen + aantal → maak leverbon)
 *
 * Props:
 *   bestelbonId: string (bv. "BB-2026-00012")
 *   onClose: () => void
 *   onCreated: (leverbon_id) => void  // wordt aangeroepen na succesvol nieuwe leverbon
 */
export default function DeelLeveringModal({ bestelbonId, onClose, onCreated }) {
  const [voortgang, setVoortgang] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectie, setSelectie] = useState({});  // { lijn_idx: aantal }
  const [busy, setBusy] = useState(false);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [chauffeurId, setChauffeurId] = useState('');
  // 📅 Geplande leveringsdatum — default = morgen 09:00 (lokale tijd)
  const [geplandeDatum, setGeplandeDatum] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    // datetime-local format: YYYY-MM-DDTHH:MM
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, ch] = await Promise.all([
        API.get(`/bestelbon/${bestelbonId}/voortgang`),
        API.get('/chauffeurs').catch(() => ({ data: { items: [] } })),
      ]);
      setVoortgang(v.data);
      setChauffeurs(ch.data?.items || []);
      // Lege selectie — gebruiker vinkt zelf aan wat hij wil leveren
      setSelectie({});
    } catch (e) {
      toast.error('Kon voortgang niet laden: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [bestelbonId]);

  useEffect(() => { load(); }, [load]);

  const updateAantal = (idx, val) => {
    const lijn = voortgang.lijnen.find(l => l.lijn_idx === idx);
    if (!lijn) return;
    const aantal = Math.max(0, Math.min(lijn.te_leveren, parseFloat(val) || 0));
    setSelectie(s => ({ ...s, [idx]: aantal }));
  };

  const toggleLijn = (idx) => {
    const lijn = voortgang.lijnen.find(l => l.lijn_idx === idx);
    if (!lijn || lijn.te_leveren <= 0) return;
    setSelectie(s => {
      const huidig = s[idx];
      if (huidig && huidig > 0) {
        const next = { ...s };
        delete next[idx];
        return next;
      }
      return { ...s, [idx]: lijn.te_leveren };
    });
  };

  const totaalGekozen = useMemo(() => {
    return Object.values(selectie).reduce((a, b) => a + (Number(b) || 0), 0);
  }, [selectie]);

  const maakLeverbon = async () => {
    const lijnen = Object.entries(selectie)
      .map(([idx, aantal]) => ({ lijn_idx: parseInt(idx, 10), aantal: Number(aantal) }))
      .filter(l => l.aantal > 0);
    if (!lijnen.length) { toast.error('Kies minstens 1 artikel met aantal > 0'); return; }
    setBusy(true);
    try {
      const r = await API.post(`/bestelbon/${bestelbonId}/maak-deellevering`, {
        lijnen,
        chauffeur_id: chauffeurId || undefined,
        gepland_leveringsdatum: geplandeDatum || undefined,
      });
      if (r.data?.ok) {
        toast.success(`Leverbon ${r.data.leverbon_id} aangemaakt met ${r.data.lijnen_count} artikel(en)`);
        onCreated?.(r.data.leverbon_id);
        await load();  // herlaad voortgang
      }
    } catch (e) {
      toast.error('Mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  if (loading || !voortgang) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-lg p-8 shadow-2xl">Laden...</div>
      </div>
    );
  }

  const procent = voortgang.totaal_besteld > 0
    ? Math.round((voortgang.totaal_geleverd / voortgang.totaal_besteld) * 100)
    : 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="deellevering-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="px-5 py-4 border-b bg-gradient-to-r from-blue-50 to-cyan-50 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
              <Truck size={20} weight="duotone" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900 truncate">
                Gedeeltelijke levering — Bestelbon {voortgang.bestelbon_id}
              </h3>
              <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-3 flex-wrap">
                <span>📦 Besteld: <b>{voortgang.totaal_besteld}</b></span>
                <span className="text-green-700">✓ Geleverd: <b>{voortgang.totaal_geleverd}</b></span>
                <span className="text-amber-700">⏳ Te leveren: <b>{voortgang.totaal_te_leveren}</b></span>
                {voortgang.volledig_geleverd && (
                  <span className="text-green-700 font-bold inline-flex items-center gap-1">
                    <CheckCircle size={12} weight="fill" /> Volledig geleverd
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-blue-100 rounded flex-shrink-0" data-testid="deellevering-close">
            <X size={18} />
          </button>
        </div>

        {/* Voortgangsbalk */}
        <div className="px-5 py-2 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${voortgang.volledig_geleverd ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${procent}%` }}
              />
            </div>
            <span className="text-xs font-bold text-gray-700 min-w-[40px] text-right">{procent}%</span>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* ─── SECTIE 1: BESTELLING + VOORTGANG ──────────────────── */}
          <section>
            <h4 className="text-sm font-bold text-gray-700 mb-2 inline-flex items-center gap-1.5">
              <Package size={16} weight="duotone" className="text-blue-600" />
              1️⃣ Bestelling — voortgang per artikel
            </h4>
            <div className="border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700">Artikel</th>
                    <th className="px-2 py-1.5 text-right text-xs font-bold text-gray-700">Besteld</th>
                    <th className="px-2 py-1.5 text-right text-xs font-bold text-green-700">Geleverd</th>
                    <th className="px-2 py-1.5 text-right text-xs font-bold text-amber-700">Te leveren</th>
                    <th className="px-2 py-1.5 text-center text-xs font-bold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {voortgang.lijnen.map(l => (
                    <tr key={l.lijn_idx} className={`border-b border-gray-100 ${l.volledig_geleverd ? 'bg-green-50/50' : ''}`} data-testid={`voortgang-lijn-${l.lijn_idx}`}>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {(l.referentie || l.ref_search) && (
                            <span className="text-[11px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded whitespace-nowrap flex-shrink-0" title="Artikel referentie">
                              {l.referentie || l.ref_search}
                            </span>
                          )}
                          <div className="text-sm font-semibold text-gray-900 max-w-[380px]" title={l.omschr}>
                            {l.omschr}
                          </div>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">{l.eenheid || ''}</div>
                      </td>
                      <td className="px-2 py-1.5 text-right text-sm font-semibold">{l.besteld}</td>
                      <td className="px-2 py-1.5 text-right text-sm font-bold text-green-700">{l.geleverd}</td>
                      <td className="px-2 py-1.5 text-right text-sm font-bold text-amber-700">{l.te_leveren}</td>
                      <td className="px-2 py-1.5 text-center">
                        {l.volledig_geleverd
                          ? <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase px-1.5 py-0.5 bg-green-100 text-green-800 rounded"><CheckCircle size={9} weight="fill" /> Klaar</span>
                          : l.geleverd > 0
                            ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">Deels</span>
                            : <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">Wachtend</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── SECTIE 2: AL GELEVERDE LEVERBONNEN ──────────────── */}
          <section>
            <h4 className="text-sm font-bold text-gray-700 mb-2 inline-flex items-center gap-1.5">
              <CheckCircle size={16} weight="duotone" className="text-green-600" />
              2️⃣ Al gegenereerde leverbonnen ({voortgang.aantal_leverbonnen})
            </h4>
            {voortgang.leverbonnen.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-xs bg-gray-50 rounded border border-gray-200">
                Nog geen leverbonnen voor deze bestelbon — eerste deellevering maken hieronder.
              </div>
            ) : (
              <div className="space-y-1">
                {voortgang.leverbonnen.map(lb => {
                  const aant = (lb.lijnen || []).reduce((a, l) => a + (Number(l.aantal) || 0), 0);
                  return (
                    <div key={lb.id} className="flex items-center justify-between px-3 py-2 bg-green-50/50 border border-green-200 rounded">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs font-bold text-gray-900">{lb.id}</span>
                        <span className="text-xs text-gray-500">{lb.datum}</span>
                        <span className="text-xs text-gray-700">{aant} stuks · €{(lb.totaal || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</span>
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                          lb.status === 'geleverd' ? 'bg-green-200 text-green-900' :
                          lb.status === 'verzonden' ? 'bg-blue-200 text-blue-900' :
                          'bg-gray-200 text-gray-700'
                        }`}>{lb.status}</span>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <a
                          href={`/api/documenten/${lb.id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-green-100 rounded text-red-600"
                          title="Open PDF"
                          data-testid={`voortgang-leverbon-pdf-${lb.id}`}
                        >
                          <FilePdf size={14} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ─── SECTIE 3: NIEUWE LEVERING SAMENSTELLEN ──────────── */}
          {!voortgang.volledig_geleverd && (
            <section>
              <h4 className="text-sm font-bold text-gray-700 mb-2 inline-flex items-center gap-1.5">
                <ArrowRight size={16} weight="bold" className="text-purple-600" />
                3️⃣ Nieuwe levering samenstellen
              </h4>
              <div className="border-2 border-purple-300 rounded p-3 bg-purple-50/30">
                <div className="space-y-1.5 mb-3">
                  {voortgang.lijnen.filter(l => l.te_leveren > 0).map(l => {
                    const aant = selectie[l.lijn_idx] || 0;
                    const aangevinkt = aant > 0;
                    return (
                      <div key={l.lijn_idx} className={`flex items-center gap-2 p-2 rounded border ${aangevinkt ? 'border-purple-400 bg-white' : 'border-gray-200 bg-white/60'}`}>
                        <input
                          type="checkbox"
                          checked={aangevinkt}
                          onChange={() => toggleLijn(l.lijn_idx)}
                          className="w-4 h-4 cursor-pointer"
                          data-testid={`deellevering-toggle-${l.lijn_idx}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {(l.referentie || l.ref_search) && (
                              <span className="text-[11px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded whitespace-nowrap flex-shrink-0" title="Artikel referentie">
                                {l.referentie || l.ref_search}
                              </span>
                            )}
                            <div className="text-sm font-semibold text-gray-900" title={l.omschr}>
                              {l.omschr}
                            </div>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{l.eenheid || ''} · max {l.te_leveren} te leveren</div>
                        </div>
                        <input
                          type="number"
                          min="0"
                          max={l.te_leveren}
                          step="any"
                          value={aant || ''}
                          onChange={e => updateAantal(l.lijn_idx, e.target.value)}
                          placeholder="0"
                          className="w-20 px-2 py-1 text-center text-sm font-bold border border-gray-300 rounded"
                          data-testid={`deellevering-aantal-${l.lijn_idx}`}
                        />
                        <span className="text-xs text-gray-500 w-16">van {l.te_leveren}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 pt-3 border-t border-purple-200 flex-wrap">
                  <label className="text-xs inline-flex items-center gap-1.5">
                    🚚 Chauffeur:
                    <select
                      value={chauffeurId}
                      onChange={e => setChauffeurId(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                      data-testid="deellevering-chauffeur"
                    >
                      <option value="">— Geen toewijzing —</option>
                      {chauffeurs.map(c => <option key={c.id} value={c.id}>{c.naam}</option>)}
                    </select>
                  </label>
                  <label className="text-xs inline-flex items-center gap-1.5">
                    📅 Geplande levering:
                    <input
                      type="datetime-local"
                      value={geplandeDatum}
                      onChange={e => setGeplandeDatum(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                      data-testid="deellevering-gepland-datum"
                    />
                    {geplandeDatum && (
                      <button
                        type="button"
                        onClick={() => setGeplandeDatum('')}
                        className="text-[10px] text-gray-500 hover:text-red-600 underline"
                        title="Wis geplande datum"
                      >
                        wis
                      </button>
                    )}
                  </label>
                  <div className="ml-auto text-xs text-gray-700">
                    Totaal: <b className="text-purple-700">{totaalGekozen}</b> stuks gekozen
                  </div>
                  <button
                    onClick={maakLeverbon}
                    disabled={busy || totaalGekozen <= 0}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white text-sm font-bold rounded inline-flex items-center gap-2"
                    data-testid="deellevering-maak-btn"
                  >
                    <Truck size={14} weight="fill" />
                    {busy ? 'Bezig...' : `Maak leverbon voor ${totaalGekozen} stuks`}
                  </button>
                </div>
              </div>
            </section>
          )}

          {voortgang.volledig_geleverd && (
            <div className="bg-green-50 border-2 border-green-300 rounded p-4 text-center">
              <CheckCircle size={32} weight="duotone" className="text-green-600 mx-auto mb-2" />
              <h4 className="font-bold text-green-900">Bestelling volledig geleverd 🎉</h4>
              <p className="text-xs text-green-700 mt-1">Alle artikelen zijn geleverd via {voortgang.aantal_leverbonnen} leverbon(nen).</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-[11px] text-gray-500 inline-flex items-center gap-1">
            <Clock size={12} /> Voortgang wordt automatisch bijgewerkt na elke nieuwe leverbon
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="deellevering-sluiten">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
